import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './Modal.themes';
type ThemeReturn = ReturnType<typeof themes>;
export type ModalProps = {
  addendum?: ReactNode;
  ariaLabel?: string;
  ariaLabelledby?: string;
  children?: ReactNode;
  className?: string;
  closeLabel?: string;
  header?: ReactNode;
  id?: string;
  onClose?: (e: React.SyntheticEvent<any>) => void;
  portalName?: string;
  size?: 'small' | 'medium' | 'large' | 'auto';
  theme?: ThemeKey<ThemeReturn>;
  dataTestId?: string;
  hasCloseButton?: boolean;
};
export const Modal = ({
  addendum,
  ariaLabel,
  ariaLabelledby,
  children,
  className,
  closeLabel,
  hasCloseButton = true,
  header,
  id,
  onClose,
  portalName,
  dataTestId = 'Modal',
  size = 'medium',
  theme = 'default',
}: ModalProps) => {
  const modalButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeBtnModal = closeLabel && ariaLabel ? [closeLabel, ariaLabel].join(' ') : closeLabel;
  useEffect(() => {
    modalButtonRef.current && modalButtonRef.current.focus();
  }, []);
  const {
    thAddendum,
    thAddendumContent,
    thBody,
    thCloseButton,
    thCloseButtonLabel,
    thCloseIconWidth,
    thContent,
    thRoot,
  } = themes({
    size,
  })[theme] as DefaultThemeStyles<ThemeReturn>;
  return (
    <Backdrop
      ariaLabel={ariaLabel}
      ariaLabelledby={ariaLabelledby}
      onClose={onClose}
      portalName={portalName}
    >
      <aside className={thRoot} id={id} data-testid={dataTestId}>
        {hasCloseButton && (
          <button
            className={thCloseButton}
            data-testid="Modal-close"
            onClick={onClose}
            ref={modalButtonRef}
            type="button"
          >
            {closeBtnModal && <span className={thCloseButtonLabel}>{closeBtnModal}</span>}
            <Icon name={ICONS.CROSS} width={thCloseIconWidth} />
          </button>
        )}
        <div className={thBody}>
          {header}
          <div className={classnames(thContent, className)}>{children}</div>
        </div>
        {addendum && (
          <div className={thAddendum}>
            <div className={classnames(thAddendumContent, className)}>{addendum}</div>
          </div>
        )}
      </aside>
    </Backdrop>
  );
};
